import { useSyncExternalStore } from "react";

/**
 * Whether the browser thinks it has a network.
 *
 * `navigator.onLine` is narrower than it sounds: `false` is trustworthy — the
 * machine has no route out at all — but `true` only means there is *an*
 * interface up. A captive portal, a VPN that dropped, an API that is down: all
 * of those are "online".
 *
 * So this decides one thing and no more: whether to say "no internet" or
 * "cannot reach the server". Getting that distinction right is most of the
 * difference between a message someone can act on and one that sends them to
 * restart a router that was never the problem.
 *
 * `useSyncExternalStore` rather than `useState` + two listeners: it subscribes
 * and reads in one place, and it cannot render a stale value on the first
 * paint after a tab is restored.
 */

function subscribe(onChange: () => void): () => void {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

function getSnapshot(): boolean {
  return navigator.onLine;
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}
